import React, { useState, useRef, useEffect } from "react";
import { Home, Plus, Trash2 } from "lucide-react";

const ForceSimulator = () => {
  const [mode, setMode] = useState("composition"); // 'composition', 'decomposition', 'walking'
  const [visualMethod, setVisualMethod] = useState("parallelogram"); // 'parallelogram' or 'triangle'
  const [showResultant, setShowResultant] = useState(true); // 是否显示合力/总位移
  const canvasRef = useRef(null);

  // 多个力的合成
  const [forces, setForces] = useState([
    { id: 1, magnitude: 60, angle: 30, color: "#3b82f6" },
    { id: 2, magnitude: 80, angle: 120, color: "#10b981" },
  ]);

  const [nextId, setNextId] = useState(3);

  // 力的分解模式
  const [mainForce, setMainForce] = useState({ magnitude: 100, angle: 45 });
  const [decompAngle1, setDecompAngle1] = useState(0);
  const [decompAngle2, setDecompAngle2] = useState(90);

  // 走路模式
  const [walkingSteps, setWalkingSteps] = useState([
    { id: 1, distance: 50, angle: 0 },
    { id: 2, distance: 60, angle: 90 },
    { id: 3, distance: 50, angle: 180 },
    { id: 4, distance: 60, angle: 270 },
  ]);
  const [nextWalkId, setNextWalkId] = useState(5);

  const centerX = 300;
  const centerY = 300;
  const scale = 1.5;

  const colors = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ec4899",
    "#06b6d4",
  ];

  const polarToCartesian = (magnitude, angle) => {
    const rad = (angle * Math.PI) / 180;
    return {
      x: magnitude * Math.cos(rad) * scale,
      y: -magnitude * Math.sin(rad) * scale,
    };
  };

  const calculateResultant = (forceList) => {
    let totalX = 0;
    let totalY = 0;
    forceList.forEach((force) => {
      const f = polarToCartesian(force.magnitude, force.angle);
      totalX += f.x;
      totalY += f.y;
    });
    const magnitude = Math.sqrt(totalX * totalX + totalY * totalY) / scale;
    const angle = (Math.atan2(-totalY, totalX) * 180) / Math.PI;
    return { magnitude, angle, x: totalX, y: totalY };
  };

  const calculateComponents = () => {
    const main = polarToCartesian(mainForce.magnitude, mainForce.angle);
    const angle1Rad = (decompAngle1 * Math.PI) / 180;
    const angle2Rad = (decompAngle2 * Math.PI) / 180;
    const mainAngleRad = (mainForce.angle * Math.PI) / 180;

    const angleOpposite1 = mainAngleRad - angle1Rad;
    const angleOpposite2 = angle2Rad - mainAngleRad;

    let mag1, mag2;

    if (Math.abs(angle2Rad - angle1Rad) < 0.01) {
      mag1 = mainForce.magnitude / 2;
      mag2 = mainForce.magnitude / 2;
    } else {
      const sinDiff = Math.sin(angle2Rad - angle1Rad);
      mag1 = (mainForce.magnitude * Math.sin(angleOpposite2)) / sinDiff;
      mag2 = (mainForce.magnitude * Math.sin(angleOpposite1)) / sinDiff;
    }

    return {
      comp1: { magnitude: Math.abs(mag1), angle: decompAngle1 },
      comp2: { magnitude: Math.abs(mag2), angle: decompAngle2 },
    };
  };

  const drawArrow = (
    ctx,
    fromX,
    fromY,
    toX,
    toY,
    color,
    label,
    thickness = 3
  ) => {
    const headLength = 15;
    const angle = Math.atan2(toY - fromY, toX - fromX);

    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = thickness;

    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(toX, toY);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(
      toX - headLength * Math.cos(angle - Math.PI / 6),
      toY - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      toX - headLength * Math.cos(angle + Math.PI / 6),
      toY - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.closePath();
    ctx.fill();

    if (label) {
      ctx.font = "bold 14px Arial";
      ctx.fillStyle = color;
      const labelX = toX + 25 * Math.cos(angle);
      const labelY = toY + 25 * Math.sin(angle);
      ctx.fillText(label, labelX, labelY);
    }
  };

  const drawDashedLine = (ctx, fromX, fromY, toX, toY, color) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(toX, toY);
    ctx.stroke();
    ctx.setLineDash([]);
  };

  const drawHouse = (ctx, x, y, size = 20) => {
    // 绘制房子
    ctx.fillStyle = "#8b5cf6";
    ctx.beginPath();
    ctx.moveTo(x, y - size);
    ctx.lineTo(x + size, y);
    ctx.lineTo(x + size, y + size);
    ctx.lineTo(x - size, y + size);
    ctx.lineTo(x - size, y);
    ctx.closePath();
    ctx.fill();

    // 房顶
    ctx.fillStyle = "#7c3aed";
    ctx.beginPath();
    ctx.moveTo(x, y - size * 1.5);
    ctx.lineTo(x + size * 1.2, y);
    ctx.lineTo(x - size * 1.2, y);
    ctx.closePath();
    ctx.fill();
  };

  const drawPerson = (ctx, x, y, size = 12) => {
    // 画一个可爱的小狗
    // 身体
    ctx.fillStyle = "#f59e0b";
    ctx.beginPath();
    ctx.ellipse(x, y, size * 1.2, size * 0.9, 0, 0, 2 * Math.PI);
    ctx.fill();

    // 头
    ctx.fillStyle = "#f59e0b";
    ctx.beginPath();
    ctx.arc(x, y - size * 0.8, size * 0.8, 0, 2 * Math.PI);
    ctx.fill();

    // 左耳朵
    ctx.fillStyle = "#d97706";
    ctx.beginPath();
    ctx.ellipse(
      x - size * 0.5,
      y - size * 1.3,
      size * 0.4,
      size * 0.6,
      -0.3,
      0,
      2 * Math.PI
    );
    ctx.fill();

    // 右耳朵
    ctx.beginPath();
    ctx.ellipse(
      x + size * 0.5,
      y - size * 1.3,
      size * 0.4,
      size * 0.6,
      0.3,
      0,
      2 * Math.PI
    );
    ctx.fill();

    // 眼睛
    ctx.fillStyle = "#000";
    ctx.beginPath();
    ctx.arc(x - size * 0.3, y - size * 0.9, size * 0.15, 0, 2 * Math.PI);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x + size * 0.3, y - size * 0.9, size * 0.15, 0, 2 * Math.PI);
    ctx.fill();

    // 鼻子
    ctx.fillStyle = "#000";
    ctx.beginPath();
    ctx.arc(x, y - size * 0.6, size * 0.2, 0, 2 * Math.PI);
    ctx.fill();

    // 尾巴
    ctx.strokeStyle = "#d97706";
    ctx.lineWidth = size * 0.3;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x + size * 0.9, y);
    ctx.quadraticCurveTo(
      x + size * 1.5,
      y - size * 0.5,
      x + size * 1.3,
      y - size
    );
    ctx.stroke();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    // 提高canvas清晰度
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, rect.width, rect.height);

    if (mode === "walking") {
      // 走路模式
      drawHouse(ctx, centerX, centerY);

      let currentX = centerX;
      let currentY = centerY;

      // 绘制每一步
      walkingSteps.forEach((step, index) => {
        const stepVec = polarToCartesian(step.distance, step.angle);
        const nextX = currentX + stepVec.x;
        const nextY = currentY + stepVec.y;

        const color = colors[index % colors.length];
        const label = `第${index + 1}步`;
        drawArrow(ctx, currentX, currentY, nextX, nextY, color, label, 3);

        currentX = nextX;
        currentY = nextY;
      });

      // 绘制最终位置的人
      drawPerson(ctx, currentX, currentY);

      // 绘制合位移（从起点到终点的直线）
      if (showResultant) {
        const resultant = calculateResultant(
          walkingSteps.map((s) => ({ magnitude: s.distance, angle: s.angle }))
        );
        if (resultant.magnitude > 1) {
          drawArrow(
            ctx,
            centerX,
            centerY,
            centerX + resultant.x,
            centerY + resultant.y,
            "#ef4444",
            `总位移=${resultant.magnitude.toFixed(1)}m`,
            5
          );
        } else {
          // 回到原点
          ctx.font = "bold 20px Arial";
          ctx.fillStyle = "#10b981";
          ctx.fillText("✓ 回到家了！", centerX + 30, centerY - 30);
        }
      }
    } else if (mode === "composition") {
      // 力的合成
      ctx.fillStyle = "#333";
      ctx.beginPath();
      ctx.arc(centerX, centerY, 5, 0, 2 * Math.PI);
      ctx.fill();

      const resultant = calculateResultant(forces);

      if (visualMethod === "parallelogram" && forces.length === 2) {
        // 平行四边形法则（只对两个力）
        const f1 = polarToCartesian(forces[0].magnitude, forces[0].angle);
        const f2 = polarToCartesian(forces[1].magnitude, forces[1].angle);

        drawDashedLine(
          ctx,
          centerX + f1.x,
          centerY + f1.y,
          centerX + f1.x + f2.x,
          centerY + f1.y + f2.y,
          "#999"
        );
        drawDashedLine(
          ctx,
          centerX + f2.x,
          centerY + f2.y,
          centerX + f1.x + f2.x,
          centerY + f1.y + f2.y,
          "#999"
        );

        forces.forEach((force, index) => {
          const f = polarToCartesian(force.magnitude, force.angle);
          drawArrow(
            ctx,
            centerX,
            centerY,
            centerX + f.x,
            centerY + f.y,
            force.color,
            `F${index + 1}=${force.magnitude.toFixed(0)}N`
          );
        });
      } else if (visualMethod === "triangle") {
        // 三角形法则（首尾相连）
        let currentX = centerX;
        let currentY = centerY;

        forces.forEach((force, index) => {
          const f = polarToCartesian(force.magnitude, force.angle);
          const nextX = currentX + f.x;
          const nextY = currentY + f.y;

          drawArrow(
            ctx,
            currentX,
            currentY,
            nextX,
            nextY,
            force.color,
            `F${index + 1}=${force.magnitude.toFixed(0)}N`
          );

          currentX = nextX;
          currentY = nextY;
        });

        // 绘制从起点到终点的虚线
        if (forces.length > 1) {
          drawDashedLine(ctx, centerX, centerY, currentX, currentY, "#999");
        }
      } else {
        // 默认：所有力从原点出发
        forces.forEach((force, index) => {
          const f = polarToCartesian(force.magnitude, force.angle);
          drawArrow(
            ctx,
            centerX,
            centerY,
            centerX + f.x,
            centerY + f.y,
            force.color,
            `F${index + 1}=${force.magnitude.toFixed(0)}N`
          );
        });
      }

      // 绘制合力
      if (showResultant) {
        drawArrow(
          ctx,
          centerX,
          centerY,
          centerX + resultant.x,
          centerY + resultant.y,
          "#ef4444",
          `F合=${resultant.magnitude.toFixed(1)}N`,
          5
        );
      }
    } else {
      // 力的分解
      ctx.fillStyle = "#333";
      ctx.beginPath();
      ctx.arc(centerX, centerY, 5, 0, 2 * Math.PI);
      ctx.fill();

      const main = polarToCartesian(mainForce.magnitude, mainForce.angle);
      const components = calculateComponents();
      const comp1 = polarToCartesian(
        components.comp1.magnitude,
        components.comp1.angle
      );
      const comp2 = polarToCartesian(
        components.comp2.magnitude,
        components.comp2.angle
      );

      if (visualMethod === "parallelogram") {
        drawDashedLine(
          ctx,
          centerX + comp1.x,
          centerY + comp1.y,
          centerX + main.x,
          centerY + main.y,
          "#999"
        );
        drawDashedLine(
          ctx,
          centerX + comp2.x,
          centerY + comp2.y,
          centerX + main.x,
          centerY + main.y,
          "#999"
        );
      } else {
        // 三角形法则
        drawArrow(
          ctx,
          centerX,
          centerY,
          centerX + comp1.x,
          centerY + comp1.y,
          "#3b82f6",
          `F₁=${components.comp1.magnitude.toFixed(1)}N`
        );
        drawArrow(
          ctx,
          centerX + comp1.x,
          centerY + comp1.y,
          centerX + main.x,
          centerY + main.y,
          "#10b981",
          `F₂=${components.comp2.magnitude.toFixed(1)}N`
        );
        drawDashedLine(
          ctx,
          centerX,
          centerY,
          centerX + main.x,
          centerY + main.y,
          "#999"
        );
      }

      if (visualMethod === "parallelogram") {
        drawArrow(
          ctx,
          centerX,
          centerY,
          centerX + main.x,
          centerY + main.y,
          "#ef4444",
          `F=${mainForce.magnitude.toFixed(0)}N`,
          4
        );
        drawArrow(
          ctx,
          centerX,
          centerY,
          centerX + comp1.x,
          centerY + comp1.y,
          "#3b82f6",
          `F₁=${components.comp1.magnitude.toFixed(1)}N`
        );
        drawArrow(
          ctx,
          centerX,
          centerY,
          centerX + comp2.x,
          centerY + comp2.y,
          "#10b981",
          `F₂=${components.comp2.magnitude.toFixed(1)}N`
        );
      } else {
        drawArrow(
          ctx,
          centerX,
          centerY,
          centerX + main.x,
          centerY + main.y,
          "#ef4444",
          `F=${mainForce.magnitude.toFixed(0)}N`,
          4
        );
      }
    }
  }, [
    mode,
    forces,
    mainForce,
    decompAngle1,
    decompAngle2,
    visualMethod,
    walkingSteps,
    showResultant,
  ]);

  const addForce = () => {
    const newColor = colors[(nextId - 1) % colors.length];
    setForces([
      ...forces,
      { id: nextId, magnitude: 50, angle: 0, color: newColor },
    ]);
    setNextId(nextId + 1);
  };

  const removeForce = (id) => {
    if (forces.length > 1) {
      setForces(forces.filter((f) => f.id !== id));
    }
  };

  const updateForce = (id, field, value) => {
    setForces(forces.map((f) => (f.id === id ? { ...f, [field]: value } : f)));
  };

  const addWalkingStep = () => {
    setWalkingSteps([
      ...walkingSteps,
      {
        id: nextWalkId,
        distance: 50,
        angle: 0,
      },
    ]);
    setNextWalkId(nextWalkId + 1);
  };

  const removeWalkingStep = (id) => {
    if (walkingSteps.length > 1) {
      setWalkingSteps(walkingSteps.filter((s) => s.id !== id));
    }
  };

  const updateWalkingStep = (id, field, value) => {
    setWalkingSteps(
      walkingSteps.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg">
      <h1 className="text-3xl font-bold text-center mb-6 text-indigo-900">
        力的合成与分解模拟器
      </h1>

      <div className="flex gap-6">
        <div className="flex-1 bg-white rounded-lg shadow-md p-4">
          <canvas
            ref={canvasRef}
            width={600}
            height={600}
            className="border-2 border-gray-200 rounded"
            style={{ width: "600px", height: "600px" }}
          />

          {mode !== "walking" && (
            <div className="mt-4 flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium">可视化方法：</span>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={visualMethod === "parallelogram"}
                  onChange={() => setVisualMethod("parallelogram")}
                  className="w-4 h-4"
                />
                <span>平行四边形法则</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={visualMethod === "triangle"}
                  onChange={() => setVisualMethod("triangle")}
                  className="w-4 h-4"
                />
                <span>三角形法则</span>
              </label>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showResultant}
                onChange={(e) => setShowResultant(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="font-medium">
                显示
                {mode === "walking"
                  ? "总位移"
                  : mode === "composition"
                  ? "合力"
                  : "主力"}
              </span>
            </label>
          </div>
        </div>

        <div className="w-96 space-y-4 overflow-y-auto max-h-[650px]">
          <div className="bg-white rounded-lg shadow-md p-4">
            <h3 className="font-bold text-lg mb-3 text-gray-800">模式选择</h3>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setMode("composition")}
                className={`py-2 px-3 rounded-lg font-medium text-sm transition ${
                  mode === "composition"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                力的合成
              </button>
              <button
                onClick={() => setMode("decomposition")}
                className={`py-2 px-3 rounded-lg font-medium text-sm transition ${
                  mode === "decomposition"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                力的分解
              </button>
              <button
                onClick={() => setMode("walking")}
                className={`py-2 px-3 rounded-lg font-medium text-sm transition flex items-center justify-center gap-1 ${
                  mode === "walking"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                <Home size={16} />
                走路
              </button>
            </div>
          </div>

          {mode === "composition" && (
            <div className="bg-white rounded-lg shadow-md p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-lg text-gray-800">调整各个力</h3>
                <button
                  onClick={addForce}
                  className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                >
                  <Plus size={16} />
                  添加力
                </button>
              </div>

              {forces.map((force, index) => (
                <div
                  key={force.id}
                  className="p-3 border-2 rounded-lg"
                  style={{ borderColor: force.color }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold" style={{ color: force.color }}>
                      F{index + 1}
                    </span>
                    {forces.length > 1 && (
                      <button
                        onClick={() => removeForce(force.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <label className="block text-sm mb-1">
                    大小: {force.magnitude.toFixed(0)} N
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="150"
                    value={force.magnitude}
                    onChange={(e) =>
                      updateForce(force.id, "magnitude", Number(e.target.value))
                    }
                    className="w-full mb-2"
                  />

                  <label className="block text-sm mb-1">
                    角度: {force.angle.toFixed(0)}°
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={force.angle}
                    onChange={(e) =>
                      updateForce(force.id, "angle", Number(e.target.value))
                    }
                    className="w-full"
                  />
                </div>
              ))}

              <div className="pt-2 border-t">
                <div className="text-sm text-gray-700 bg-red-50 p-3 rounded">
                  <span className="font-bold text-red-600">合力 F：</span>
                  <div className="mt-1">
                    大小 = {calculateResultant(forces).magnitude.toFixed(1)} N
                  </div>
                  <div>
                    方向 = {calculateResultant(forces).angle.toFixed(1)}°
                  </div>
                </div>
              </div>
            </div>
          )}

          {mode === "decomposition" && (
            <div className="bg-white rounded-lg shadow-md p-4 space-y-4">
              <h3 className="font-bold text-lg text-gray-800">调整力的分解</h3>

              <div>
                <label className="block text-sm font-medium text-red-600 mb-2">
                  主力大小: {mainForce.magnitude.toFixed(0)} N
                </label>
                <input
                  type="range"
                  min="0"
                  max="150"
                  value={mainForce.magnitude}
                  onChange={(e) =>
                    setMainForce({
                      ...mainForce,
                      magnitude: Number(e.target.value),
                    })
                  }
                  className="w-full"
                />

                <label className="block text-sm font-medium text-red-600 mb-2 mt-3">
                  主力角度: {mainForce.angle.toFixed(0)}°
                </label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={mainForce.angle}
                  onChange={(e) =>
                    setMainForce({
                      ...mainForce,
                      angle: Number(e.target.value),
                    })
                  }
                  className="w-full"
                />
              </div>

              <div className="pt-2 border-t">
                <label className="block text-sm font-medium text-blue-600 mb-2">
                  分力F₁方向: {decompAngle1.toFixed(0)}°
                </label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={decompAngle1}
                  onChange={(e) => setDecompAngle1(Number(e.target.value))}
                  className="w-full"
                />

                <label className="block text-sm font-medium text-green-600 mb-2 mt-3">
                  分力F₂方向: {decompAngle2.toFixed(0)}°
                </label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={decompAngle2}
                  onChange={(e) => setDecompAngle2(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="pt-2 border-t">
                <div className="text-sm text-gray-700 bg-blue-50 p-3 rounded">
                  <div className="font-bold text-blue-600">分力结果：</div>
                  <div className="mt-1">
                    F₁ = {calculateComponents().comp1.magnitude.toFixed(1)} N
                  </div>
                  <div>
                    F₂ = {calculateComponents().comp2.magnitude.toFixed(1)} N
                  </div>
                </div>
              </div>
            </div>
          )}

          {mode === "walking" && (
            <div className="bg-white rounded-lg shadow-md p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-lg text-gray-800">🚶 规划路线</h3>
                <button
                  onClick={addWalkingStep}
                  className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                >
                  <Plus size={16} />
                  添加步骤
                </button>
              </div>

              {walkingSteps.map((step, index) => (
                <div
                  key={step.id}
                  className="p-3 border-2 rounded-lg border-gray-300"
                >
                  <div className="flex justify-between items-center mb-2">
                    <span
                      className="font-bold text-sm px-2 py-1"
                      style={{ color: colors[index % colors.length] }}
                    >
                      第{index + 1}步
                    </span>
                    {walkingSteps.length > 1 && (
                      <button
                        onClick={() => removeWalkingStep(step.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <label className="block text-sm mb-1">
                    距离: {step.distance.toFixed(0)} 米
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={step.distance}
                    onChange={(e) =>
                      updateWalkingStep(
                        step.id,
                        "distance",
                        Number(e.target.value)
                      )
                    }
                    className="w-full mb-2"
                  />

                  <label className="block text-sm mb-1">
                    方向: {step.angle.toFixed(0)}°{step.angle === 0 && " (东)"}
                    {step.angle === 90 && " (北)"}
                    {step.angle === 180 && " (西)"}
                    {step.angle === 270 && " (南)"}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={step.angle}
                    onChange={(e) =>
                      updateWalkingStep(
                        step.id,
                        "angle",
                        Number(e.target.value)
                      )
                    }
                    className="w-full"
                  />
                </div>
              ))}

              <div className="pt-2 border-t">
                <div className="text-sm text-gray-700 bg-yellow-50 p-3 rounded">
                  <div className="font-bold text-yellow-800">💡 思考：</div>
                  <div className="mt-1">
                    走了这么多步，最终离家
                    {calculateResultant(
                      walkingSteps.map((s) => ({
                        magnitude: s.distance,
                        angle: s.angle,
                      }))
                    ).magnitude.toFixed(1)}
                    米
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    试着调整路线，让总位移为0，也就是回到家！
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-4">
            <h3 className="font-bold text-lg mb-2 text-gray-800">
              💡 使用说明
            </h3>
            <div className="text-sm text-gray-600 space-y-2">
              {mode === "composition" && (
                <>
                  <p>
                    • <strong>平行四边形法则</strong>：适用于2个力，从同一点出发
                  </p>
                  <p>
                    • <strong>三角形法则</strong>：首尾相连，适用于多个力
                  </p>
                  <p>• 可添加多个力观察合力变化</p>
                  <p>• 红色箭头表示最终合力</p>
                </>
              )}
              {mode === "decomposition" && (
                <>
                  <p>• 一个力可以分解为两个分力</p>
                  <p>
                    • <strong>平行四边形法则</strong>：分力从原点出发
                  </p>
                  <p>
                    • <strong>三角形法则</strong>：分力首尾相连
                  </p>
                  <p>• 改变分解方向会改变分力大小</p>
                </>
              )}
              {mode === "walking" && (
                <>
                  <p>• 🏠 红房子是你的家（起点）</p>
                  <p>• 👤 蓝色小人是你的最终位置</p>
                  <p>• 每步用不同颜色的箭头表示</p>
                  <p>• 红色粗箭头是总位移（矢量和）</p>
                  <p>
                    • <strong>目标</strong>：试着让总位移为0，回到家！
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default ForceSimulator;
